import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Alert, Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import $ from 'jquery';
import 'trumbowyg/dist/ui/trumbowyg.min.css';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import '../App.css';

window.jQuery = $;
window.$ = $;
require('trumbowyg');
require('trumbowyg/dist/plugins/fontfamily/trumbowyg.fontfamily.min.js');
require('trumbowyg/dist/plugins/fontsize/trumbowyg.fontsize.min.js');

const c_TrumbowygBtns = [
	['formatting'],
	['fontfamily', 'fontsize'],
	['strong', 'em'],
	['unorderedList', 'orderedList'],
	['link'],
	['removeformat'],
];

const c_MimeToFormat = {
	'image/png': '.png',
	'image/jpeg': '.jpeg',
	'image/gif': '.gif',
	'image/webp': '.webp',
};

const TrumbowygEditor = React.forwardRef(function TrumbowygEditor({ initial_value }, ref) {
	const wrapper_ref = useRef(null);
	const editor_el = useRef(null);

	useImperativeHandle(ref, function() {
		return {
			get_html: function() {
				return $(editor_el.current).trumbowyg('html');
			},
		};
	});

	useEffect(function() {
		const div = document.createElement('div');
		wrapper_ref.current.appendChild(div);
		editor_el.current = div;

		$(div).trumbowyg({
			btns: c_TrumbowygBtns,
			autogrow: true,
			svgPath: '/trumbowyg-icons.svg',
		});

		if(initial_value) {
			$(div).trumbowyg('html', initial_value);
		}

		return function() {
			try {
				$(div).trumbowyg('destroy');
			} catch(error) {}

			if(wrapper_ref.current) {
				wrapper_ref.current.innerHTML = '';
			}
		};
	}, []);

	return <div ref={wrapper_ref}></div>;
});

function HelpView() {
	const { key } = useParams();
	const { token, is_superadmin } = useAuth();
	const navigate = useNavigate();

	const [page, set_page] = useState(null);
	const [sections, set_sections] = useState([]);
	const [all_pages, set_all_pages] = useState([]);
	const [help_nav_open, set_help_nav_open] = useState(false);
	const help_nav_ref = useRef(null);
	const [loading, set_loading] = useState(true);
	const [error, set_error] = useState('');

	const [editing_page_title, set_editing_page_title] = useState(false);
	const [page_title_draft, set_page_title_draft] = useState('');
	const [page_image_url_draft, set_page_image_url_draft] = useState('');
	const [uploading_image, set_uploading_image] = useState(false);
	const [editing_section_id, set_editing_section_id] = useState(null);
	const [section_title_draft, set_section_title_draft] = useState('');
	const [section_image_url_draft, set_section_image_url_draft] = useState('');
	const [uploading_section_image, set_uploading_section_image] = useState(false);
	const [save_error, set_save_error] = useState('');
	const [adding_below_id, set_adding_below_id] = useState(null);
	const [deleting_section_id, set_deleting_section_id] = useState(null);
	const [confirm_delete_section, set_confirm_delete_section] = useState(null);

	const editor_ref = useRef(null);

	useEffect(function() {
		if(!key) {
			return;
		}
		set_loading(true);
		set_error('');
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		const requests = [
			axios.get(Constants.SERVER_URL + '/api/help/pages/' + key + '/sections', { headers }),
		];
		if(key === 'introduction') {
			requests.push(axios.get(Constants.SERVER_URL + '/api/help/pages', { headers }));
		}
		Promise.all(requests)
			.then(function(responses) {
				set_page(responses[0].data.page);
				set_sections(responses[0].data.sections || []);
				if(key === 'introduction' && responses[1]) {
					set_all_pages(responses[1].data.pages || []);
				}
				set_loading(false);
			})
			.catch(function() {
				set_error('Failed to load help content.');
				set_loading(false);
			});
	}, [key, token]);

	useEffect(function() {
		const handle_click_outside = function(e) {
			if(help_nav_ref.current && !help_nav_ref.current.contains(e.target)) {
				set_help_nav_open(false);
			}
		};
		document.addEventListener('mousedown', handle_click_outside);
		return function() {
			document.removeEventListener('mousedown', handle_click_outside);
		};
	}, []);

	const handle_edit_page = function() {
		set_save_error('');
		set_page_title_draft(page.title);
		set_page_image_url_draft(page.image_url || '');
		set_editing_page_title(true);
	};

	const handle_save_page = async function() {
		set_save_error('');
		const headers = { Authorization: 'Bearer ' + token };
		try {
			await axios.put(
				Constants.SERVER_URL + '/api/help/pages/' + page.id,
				{ title: page_title_draft, image_url: page_image_url_draft },
				{ headers }
			);
			set_page({ ...page, title: page_title_draft, image_url: page_image_url_draft });
			set_editing_page_title(false);
		} catch(error) {
			set_save_error('Failed to save page.');
		}
	};

	const handle_page_image_upload = async function(event) {
		const file = event.target.files[0];
		if(!file) {
			return;
		}
		const format = c_MimeToFormat[file.type];
		if(!format) {
			set_save_error('Unsupported image format.');
			return;
		}
		set_uploading_image(true);
		set_save_error('');
		try {
			const data_url = await new Promise(function(resolve) {
				const reader = new FileReader();
				reader.onload = function(e) { resolve(e.target.result); };
				reader.readAsDataURL(file);
			});
			const img = new window.Image();
			await new Promise(function(resolve) {
				img.onload = resolve;
				img.src = data_url;
			});
			const width = img.naturalWidth;
			const height = img.naturalHeight;
			const form_data = new FormData();
			form_data.append('image', file);
			form_data.append('filename', file.name);
			form_data.append('folder', 'help');
			form_data.append('format', format);
			form_data.append('width', width);
			form_data.append('height', height);
			const headers = { Authorization: 'Bearer ' + token };
			const res = await axios.post(
				Constants.SERVER_URL + '/api/images',
				form_data,
				{ headers }
			);
			set_page_image_url_draft(Constants.SERVER_URL + '/api/images/' + res.data.id);
		} catch(error) {
			set_save_error('Failed to upload image.');
		}
		set_uploading_image(false);
	};

	const handle_add_section_below = async function(section, index) {
		set_save_error('');
		set_adding_below_id(section.id);
		const next_section = sections[index + 1];
		const sort_order = next_section
			? (section.sort_order + next_section.sort_order) / 2
			: section.sort_order + 1;
		const headers = { Authorization: 'Bearer ' + token };
		try {
			const res = await axios.post(
				Constants.SERVER_URL + '/api/help/sections',
				{ page_id: page.id, title: 'New Section', sort_order },
				{ headers }
			);
			const new_section = res.data.section;
			const updated = sections.slice();
			updated.splice(index + 1, 0, new_section);
			set_sections(updated);
			set_section_title_draft(new_section.title);
			set_section_image_url_draft('');
			set_editing_section_id(new_section.id);
		} catch(error) {
			set_save_error('Failed to create section.');
		}
		set_adding_below_id(null);
	};

	const handle_delete_section = async function(section) {
		set_save_error('');
		set_deleting_section_id(section.id);
		const headers = { Authorization: 'Bearer ' + token };
		try {
			await axios.delete(
				Constants.SERVER_URL + '/api/help/sections/' + section.id,
				{ headers }
			);
			set_sections(sections.filter(function(sec) { return sec.id !== section.id; }));
		} catch(error) {
			set_save_error('Failed to delete section.');
		}
		set_deleting_section_id(null);
	};

	const handle_edit_section = function(section) {
		set_save_error('');
		set_section_title_draft(section.title);
		set_section_image_url_draft(section.image_url || '');
		set_editing_section_id(section.id);
	};

	const handle_save_section = async function(section) {
		set_save_error('');
		const html = editor_ref.current.get_html();
		const headers = { Authorization: 'Bearer ' + token };
		try {
			await axios.put(
				Constants.SERVER_URL + '/api/help/sections/' + section.id,
				{ title: section_title_draft, body: html, image_url: section_image_url_draft },
				{ headers }
			);
			set_sections(sections.map(function(sec) {
				if(sec.id !== section.id) {
					return sec;
				}
				return { ...sec, title: section_title_draft, body: html, image_url: section_image_url_draft };
			}));
			set_editing_section_id(null);
		} catch(error) {
			set_save_error('Failed to save section.');
		}
	};

	const handle_section_image_upload = async function(event) {
		const file = event.target.files[0];
		if(!file) {
			return;
		}
		const format = c_MimeToFormat[file.type];
		if(!format) {
			set_save_error('Unsupported image format.');
			return;
		}
		set_uploading_section_image(true);
		set_save_error('');
		try {
			const data_url = await new Promise(function(resolve) {
				const reader = new FileReader();
				reader.onload = function(e) { resolve(e.target.result); };
				reader.readAsDataURL(file);
			});
			const img = new window.Image();
			await new Promise(function(resolve) {
				img.onload = resolve;
				img.src = data_url;
			});
			const width = img.naturalWidth;
			const height = img.naturalHeight;
			const form_data = new FormData();
			form_data.append('image', file);
			form_data.append('filename', file.name);
			form_data.append('folder', 'help');
			form_data.append('format', format);
			form_data.append('width', width);
			form_data.append('height', height);
			const headers = { Authorization: 'Bearer ' + token };
			const res = await axios.post(
				Constants.SERVER_URL + '/api/images',
				form_data,
				{ headers }
			);
			set_section_image_url_draft(Constants.SERVER_URL + '/api/images/' + res.data.id);
		} catch(error) {
			set_save_error('Failed to upload image.');
		}
		set_uploading_section_image(false);
	};

	const render_delete_confirm_modal = function() {
		if(!confirm_delete_section) {
			return null;
		}
		return (
			<Modal show size="sm" centered onHide={function() { set_confirm_delete_section(null); }}>
				<Modal.Header>
					<Modal.Title>Delete Section</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>Delete <strong>{confirm_delete_section.title}</strong>?</p>
					<p>This cannot be undone.</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={function() { set_confirm_delete_section(null); }}
					>
						Cancel
					</Button>
					<Button
						variant="danger"
						disabled={deleting_section_id === confirm_delete_section.id}
						onClick={function() {
							handle_delete_section(confirm_delete_section);
							set_confirm_delete_section(null);
						}}
					>
						Delete
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_loading = function() {
		return (
			<div className="help-loading">
				<Spinner animation="border" size="sm" />
			</div>
		);
	};

	const render_error = function() {
		return <Alert variant="danger">{error}</Alert>;
	};

	const render_section = function(section, index) {
		const is_editing = editing_section_id === section.id;
		return (
			<div key={section.id} className="help-section">
				{!is_editing ? (
					<React.Fragment>
						<div className="help-section-title-row">
							<h5 className="help-section-title">{section.title}</h5>
							{is_superadmin ? (
								<React.Fragment>
									<button
										className="help-edit-btn"
										onClick={function() { handle_edit_section(section); }}
									>
										Edit
									</button>
									<button
										className="help-add-btn"
										disabled={adding_below_id === section.id}
										onClick={function() { handle_add_section_below(section, index); }}
									>
										{adding_below_id === section.id ? 'Adding...' : 'Add Section'}
									</button>
									<button
										className="help-delete-btn"
										disabled={deleting_section_id === section.id}
										onClick={function() { set_confirm_delete_section(section); }}
									>
										{deleting_section_id === section.id ? 'Deleting...' : 'Delete'}
									</button>
								</React.Fragment>
							) : null}
						</div>
						{section.image_url ? (
							<img
								src={section.image_url}
								alt={section.title}
								className="help-section-image"
							/>
						) : section.image_url === null ? (
							<div className="help-section-image-placeholder">
								<span>Image coming soon</span>
							</div>
						) : null}
						{section.body ? (
							<div
								className="help-section-body"
								dangerouslySetInnerHTML={{ __html: section.body }}
							/>
						) : null}
					</React.Fragment>
				) : null}
				{is_editing ? (
					<React.Fragment>
						<input
							className="help-edit-title-input"
							value={section_title_draft}
							onChange={function(event) { set_section_title_draft(event.target.value); }}
							onKeyDown={function(event) { if(event.key === 'Enter') { handle_save_section(section); } }}
						/>
						<div className="help-image-upload-row">
							{section_image_url_draft ? (
								<img
									src={section_image_url_draft}
									alt="Section preview"
									className="help-image-preview"
								/>
							) : null}
							{uploading_section_image ? (
								<div className="help-image-uploading">
									<Spinner animation="border" size="sm" />
									<span>Uploading...</span>
								</div>
							) : (
								<input
									type="file"
									accept="image/png,image/jpeg,image/gif,image/webp"
									className="help-image-upload-input"
									onChange={handle_section_image_upload}
								/>
							)}
							<button
								className="help-delete-image-btn"
								onClick={function() { set_section_image_url_draft(''); }}
							>
								Delete Image
							</button>
						</div>
						<TrumbowygEditor ref={editor_ref} initial_value={section.body} />
						{save_error ? <p className="help-save-error">{save_error}</p> : null}
						<div className="help-edit-actions">
							<button
								className="help-edit-save-btn"
								onClick={function() { handle_save_section(section); }}
							>
								Save
							</button>
							<button
								className="help-edit-cancel-btn"
								onClick={function() { set_editing_section_id(null); }}
							>
								Cancel
							</button>
						</div>
					</React.Fragment>
				) : null}
			</div>
		);
	};

	const render_page_editor = function() {
		return (
			<div className="help-page-title-editor">
				<input
					className="help-edit-title-input help-edit-page-title-input"
					value={page_title_draft}
					onChange={function(event) { set_page_title_draft(event.target.value); }}
					onKeyDown={function(event) { if(event.key === 'Enter') { handle_save_page(); } }}
				/>
				<div className="help-image-upload-row">
					{page_image_url_draft ? (
						<img
							src={page_image_url_draft}
							alt="Page preview"
							className="help-image-preview"
						/>
					) : null}
					{uploading_image ? (
						<div className="help-image-uploading">
							<Spinner animation="border" size="sm" />
							<span>Uploading...</span>
						</div>
					) : (
						<input
							type="file"
							accept="image/png,image/jpeg,image/gif,image/webp"
							className="help-image-upload-input"
							onChange={handle_page_image_upload}
						/>
					)}
				</div>
				{save_error ? <p className="help-save-error">{save_error}</p> : null}
				<div className="help-edit-actions">
					<button className="help-edit-save-btn" onClick={handle_save_page}>Save</button>
					<button
						className="help-edit-cancel-btn"
						onClick={function() { set_editing_page_title(false); }}
					>
						Cancel
					</button>
				</div>
			</div>
		);
	};

	const render_content = function() {
		if(!page) {
			return <p className="help-empty">No content found for this page.</p>;
		}
		return (
			<React.Fragment>
				{page.key !== 'introduction' ? (
					<Button variant="primary" size="sm" className="d-lg-none help-back-btn" onClick={function() { navigate('/help/introduction'); }}>
						Back
					</Button>
				) : null}
				{editing_page_title ? render_page_editor() : (
					<div className="help-page-title-row">
						<h4 className="help-page-title">{page.title}</h4>
						{is_superadmin ? (
							<button className="help-edit-btn" onClick={handle_edit_page}>Edit</button>
						) : null}
					</div>
				)}
				{page.key === 'introduction' && all_pages.length > 0 ? (
				<div className="help-mobile-nav d-lg-none" ref={help_nav_ref}>
					<div
						className="voice-dropdown-trigger"
						onClick={function() { set_help_nav_open(function(prev) { return !prev; }); }}>
						<span>Go to a help page...</span>
						<span className="voice-dropdown-arrow">&#9660;</span>
					</div>
					{help_nav_open ? (
						<div className="voice-dropdown-list">
							{all_pages.filter(function(p) { return p.key !== 'introduction'; }).map(function(p) {
								return (
									<div
										key={p.id}
										className="voice-dropdown-option"
										onClick={function() { set_help_nav_open(false); navigate('/help/' + p.key); }}>
										{p.title}
									</div>
								);
							})}
						</div>
					) : null}
				</div>
			) : null}
			{!editing_page_title && page.image_url ? (
					<img
						src={page.image_url}
						alt={page.title}
						className="help-page-image"
					/>
				) : null}
				{sections.length > 0 ? (
					sections.map(function(section, index) { return render_section(section, index); })
				) : (
					<p className="help-empty">No sections yet for this page.</p>
				)}
			</React.Fragment>
		);
	};

	return (
		<React.Fragment>
			{render_delete_confirm_modal()}
			<main className="help-main">
				{loading ? render_loading() : null}
				{error ? render_error() : null}
				{!loading && !error ? render_content() : null}
			</main>
		</React.Fragment>
	);
}

export default HelpView;
